import type { ReactNode } from 'react';
import AspectRatioBox from '@ingka/aspect-ratio-box';
import Image from '@ingka/image';
import Button from '@ingka/button';
import Text from '@ingka/text';
import arrowRightIcon from '@ingka/ssr-icon/paths/arrow-right';
import arrowLeftIcon from '@ingka/ssr-icon/paths/arrow-left';
import styled from 'styled-components';
import useIsRtl from '@app/hooks/useIsRtl';

export const Container = styled.div`
  width: 100%;
  height: 100%;
  line-height: 1.571;

  @media (width <= 1024px) {
    margin-bottom: 2rem;
  }
`;

// Custom card wrapper that mimics the old vertical Card layout
const CardContainer = styled.div<{ $backgroundColor?: string; $textColor?: string }>`
  display: flex;
  flex-direction: column;
  background-color: ${({ $backgroundColor }) => $backgroundColor || '#fff'};
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  height: 100%;

  &:hover {
    cursor: pointer;

    h3 {
      text-decoration: underline;
    }
  }
`;

const CardImageWrapper = styled.div`
  flex-shrink: 0;
`;

const CardContent = styled.div<{ $textColor?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  padding: 24px;
  color: ${({ $textColor }) => $textColor || '#111'};
`;

const CardTitleWrapper = styled.div<{ $textColor?: string }>`
  margin: 0.5rem 0 12px;
  color: ${({ $textColor }) => $textColor || 'inherit'};
  text-decoration: none;

  h3 {
    text-decoration: inherit;
    color: inherit;
  }
`;

const CardBodyWrapper = styled.div<{ $textColor?: string }>`
  margin: 0 0 16px;
  color: ${({ $textColor }) => $textColor || 'inherit'};

  p {
    color: inherit;
  }
`;

const CardButtonWrapper = styled.div`
  margin-top: 1rem;
`;

type StyledCardProps = {
  title: string;
  body?: string;
  'data-testid'?: string;
  emphasised?: boolean;
  backgroundColor?: string;
  textColor?: string;
  imageProps?: {
    src?: string;
    alt: string;
  };
  onClick?: () => void;
  ctaLabel?: string;
  buttonVariant?: string;
  imagePosition?: 'leading' | 'trailing';
  imageRatio?: 'square' | 'wide';
  compact?: boolean;
  children?: ReactNode;
};

export const StyledCard = ({
  title,
  body,
  'data-testid': dataTestId,
  backgroundColor,
  textColor,
  imageProps,
  onClick,
  ctaLabel,
  imageRatio = 'square'
}: StyledCardProps) => {
  const isRtl = useIsRtl();

  const ratio = imageRatio === 'wide' ? 'wide' : 'square';

  return (
    <CardContainer
      $backgroundColor={backgroundColor}
      $textColor={textColor}
      data-testid={dataTestId}
      onClick={onClick}
    >
      {imageProps?.src && (
        <CardImageWrapper>
          <AspectRatioBox ratio={ratio}>
            <Image src={imageProps.src} alt={imageProps.alt} />
          </AspectRatioBox>
        </CardImageWrapper>
      )}
      <CardContent $textColor={textColor}>
        <div>
          <CardTitleWrapper $textColor={textColor}>
            <Text tagName="h3" textStyle="Heading.M">
              {title}
            </Text>
          </CardTitleWrapper>
          {body && (
            <CardBodyWrapper $textColor={textColor}>
              <Text tagName="p" textStyle="Body.M">
                {body}
              </Text>
            </CardBodyWrapper>
          )}
        </div>
        <CardButtonWrapper>
          <Button
            type="secondary"
            inverseTheme={textColor === '#FFFFFF'}
            iconOnly
            size="small"
            ssrIcon={isRtl ? arrowLeftIcon : arrowRightIcon}
            aria-label={ctaLabel}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onClick?.();
            }}
          />
        </CardButtonWrapper>
      </CardContent>
    </CardContainer>
  );
};

export const GridContainer = styled.div`
  position: relative;
  z-index: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: min-content;
  gap: 16px;
  padding-bottom: 0;
  width: 100%;

  .aspect-ratio-box[class*='--wide'] {
    padding-bottom: 77%;
  }

  .card {
    &:hover {
      cursor: pointer;
    }
  }

  @media screen and (width <= 1024px) {
    grid-template-columns: 1fr;
    margin: 0;
  }
`;

export const CarouselContainer = styled.div`
  display: block;
  width: 100%;
  margin-bottom: 4rem;
`;

export const RangeTitleBackgroundContainer = styled.div`
  display: flex;
  position: relative;

  img {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 1;

    @media (width <= 1024px) {
      display: none;
    }
  }
`;

export const TitleWrapper = styled.div`
  z-index: 2;
  margin: 70px 56px;
`;

export const TitleWrapperMobile = styled.div`
  z-index: 2;
  margin: 10px 0;
`;

export const LandingPageTitle = styled.h2<{ color?: string }>`
  font-weight: 700;
  margin: 0;
  padding: 0;
  word-break: normal;
  overflow-wrap: anywhere;
  color: ${props => props.color || '#111'};
`;

export const SimpleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  grid-template-rows: max-content;

  @media (width <= 1024px) {
    grid-template-columns: 1fr;
  }

  .aspect-ratio-box[class*='--wide'] {
    padding-bottom: 117%;

    @media (width <= 1024px) {
      padding-bottom: 80%;
    }
  }
`;
